import axios, { AxiosError, AxiosRequestConfig } from 'axios'

// ── Base URL ────────────────────────────────────────────────────────────────
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) {
    const url = import.meta.env.VITE_API_BASE_URL
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    return url
  }
  // Por defecto, ruta relativa — Traefik enruta /api-backend al backend.
  return '/api-backend'
}

const API_BASE_URL = getApiBaseUrl()
if (!import.meta.env.PROD) console.log('API Base URL:', API_BASE_URL)

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

// ── Request: agregar Authorization ──────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  },
  (error) => Promise.reject(error),
)

// ── Response: manejar 401 con auto-refresh ──────────────────────────────────
//
// Estrategia:
// 1. Solo intentamos refresh ante 401 en endpoints de API (no en /auth/login,
//    /auth/refresh, /auth/logout, ni /auth/google-login: ahí 401 = credenciales mal).
// 2. Si varios requests fallan 401 simultáneamente, solo UN refresh corre.
//    El resto espera al resultado y se reintenta con el nuevo token.
// 3. Si el refresh falla → recién ahí se limpia sesión y se redirige a /.

let isRefreshing = false
let pendingQueue: Array<{
  resolve: (token: string) => void
  reject: (err: any) => void
}> = []

function flushQueue(token: string | null, err: any) {
  pendingQueue.forEach((p) => {
    if (token) p.resolve(token)
    else p.reject(err)
  })
  pendingQueue = []
}

function isAuthEndpoint(url?: string): boolean {
  if (!url) return false
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/google-login') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/reset-password-confirm')
  )
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined

    // Sin config (errores de red sin request) o no es 401 → propagar.
    if (!originalRequest || error.response?.status !== 401) {
      return Promise.reject(error)
    }

    // 401 en endpoint de auth → no tiene sentido refrescar; limpiar y redirigir
    // si era /auth/refresh (refresh inválido = sesión muerta).
    if (isAuthEndpoint(originalRequest.url)) {
      if (originalRequest.url?.includes('/auth/refresh')) {
        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')
        // Solo redirigir si no estamos ya en login.
        if (!window.location.pathname.startsWith('/login') && window.location.pathname !== '/') {
          window.location.href = '/'
        }
      }
      return Promise.reject(error)
    }

    // Evitar bucle infinito: si ya reintentamos este request, abortar.
    if (originalRequest._retry) {
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      if (!window.location.pathname.startsWith('/login') && window.location.pathname !== '/') {
        window.location.href = '/'
      }
      return Promise.reject(error)
    }

    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      // Sin refresh token → sesión muerta de entrada. Limpiar y redirigir.
      localStorage.removeItem('token')
      if (!window.location.pathname.startsWith('/login') && window.location.pathname !== '/') {
        window.location.href = '/'
      }
      return Promise.reject(error)
    }

    originalRequest._retry = true

    // Si ya hay un refresh en curso → encolar este request.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token: string) => {
            originalRequest.headers = originalRequest.headers ?? {}
            ;(originalRequest.headers as any).Authorization = `Bearer ${token}`
            resolve(api(originalRequest))
          },
          reject,
        })
      })
    }

    isRefreshing = true
    try {
      const { data } = await api.post<{ access_token: string; refresh_token: string }>(
        '/auth/refresh',
        { refresh_token: refreshToken },
      )
      const newAccess = data.access_token
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token)
      localStorage.setItem('token', newAccess)
      flushQueue(newAccess, null)

      originalRequest.headers = originalRequest.headers ?? {}
      ;(originalRequest.headers as any).Authorization = `Bearer ${newAccess}`
      return api(originalRequest)
    } catch (refreshErr) {
      flushQueue(null, refreshErr)
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      if (!window.location.pathname.startsWith('/login') && window.location.pathname !== '/') {
        window.location.href = '/'
      }
      return Promise.reject(refreshErr)
    } finally {
      isRefreshing = false
    }
  },
)

export default api
